import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Play, Square, Wand2, Loader2, Disc3, X, Sparkles } from "lucide-react";
import { buildChain, ensureStereo, autoMaster, analyze, bandSpectrum, matchEq, measureLufs, makeSettings, dbToGain, MODES, autoBalanceEq } from "@/lib/mastering";
import { MasteringControls } from "./MasteringControls";
import { StatsRow, Spectrum } from "./MasteringMeters";
import { MasteringExport } from "./MasteringExport";

const btn = "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,transform] hover:-translate-y-0.5 disabled:opacity-50";

// Studio shared by admin (free) and paying customers. `source` = {arrayBuffer, name}; `actions(result)` renders extra buttons.
export const MasteringStudio = ({ source, allowUpload = true, actions }) => {
  const ctxRef = useRef(null);
  const live = useRef(null);
  const [src, setSrc] = useState(null);
  const [name, setName] = useState("");
  const [inStats, setInStats] = useState(null);
  const [genre, setGenre] = useState("techhouse");
  const [mode, setMode] = useState("streaming");
  const [s, setS] = useState(() => makeSettings("techhouse", "streaming"));
  const [ab, setAb] = useState("master");
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [ref, setRef] = useState(null);
  const [auto, setAuto] = useState(false);

  const ctx = () => (ctxRef.current ||= new (window.AudioContext || window.webkitAudioContext)());
  const decode = async (ab) => ensureStereo(ctx(), await ctx().decodeAudioData(ab));

  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (source) load(source.arrayBuffer, source.name); }, [source]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { live.current?.chain.update({ ...s, drive: result?.drive ?? Math.max(0, s.target - (inStats?.lufs ?? s.target)) }); }, [s, result, inStats]);
  useEffect(() => { if (live.current) applyAb(live.current); }, [ab]); // eslint-disable-line react-hooks/exhaustive-deps

  async function load(arrayBuf, label) {
    stop(); setResult(null); setBusy("Analisi del brano…");
    try {
      const buf = await decode(arrayBuf.slice(0));
      setSrc(buf); setName(label); setInStats(await analyze(buf));
    } catch { toast.error("Impossibile leggere questo file audio"); }
    finally { setBusy(""); }
  }
  const onFile = async (e) => { const f = e.target.files?.[0]; if (f) await load(await f.arrayBuffer(), f.name.replace(/\.[^.]+$/, "")); e.target.value = ""; };
  const onRef = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !src) return;
    setBusy("Analisi del brano di riferimento…");
    try {
      const rb = await decode(await f.arrayBuffer());
      const lufs = await measureLufs(rb);
      const eq = matchEq(inStats?.bands || bandSpectrum(src), bandSpectrum(rb));
      setRef({ name: f.name.replace(/\.[^.]+$/, ""), lufs }); setAuto(false);
      setS({ ...s, eqMatch: eq, target: Math.max(-16, Math.min(-6, Math.round(lufs * 2) / 2)) });
      setResult(null);
      toast.success("Riferimento applicato: tonalità e volume abbinati");
    } catch { toast.error("Impossibile leggere il brano di riferimento"); }
    finally { setBusy(""); }
  };
  const clearRef = () => { setRef(null); setAuto(false); setResult(null); setS({ ...s, eqMatch: null, target: MODES[mode].target }); };
  const toggleAuto = () => {
    if (!inStats) return;
    const on = !auto;
    setAuto(on); setRef(null); setResult(null);
    setS({ ...s, eqMatch: on ? autoBalanceEq(inStats.bands, genre) : null });
    if (on) toast.success("Bilanciamento tonale automatico attivo");
  };

  function applyAb(l) {
    const match = dbToGain((result?.lufs ?? s.target) - (inStats?.lufs ?? s.target));
    l.dry.gain.value = ab === "orig" ? Math.min(match, 4) : 0; l.wet.gain.value = ab === "orig" ? 0 : 1;
  }
  function stop() { try { live.current?.node.stop(); } catch { /* already stopped */ } live.current = null; setPlaying(false); }
  const play = (buffer = src, rendered = false) => {
    stop();
    const c = ctx(); c.resume();
    const node = c.createBufferSource(); node.buffer = buffer;
    const chain = buildChain(c, { live: true });
    chain.update({ ...s, drive: result?.drive ?? Math.max(0, s.target - (inStats?.lufs ?? s.target)) });
    const dry = c.createGain(), wet = c.createGain();
    if (rendered) node.connect(c.destination);
    else { node.connect(dry); dry.connect(c.destination); node.connect(chain.input); chain.output.connect(wet); wet.connect(c.destination); }
    live.current = { node, chain, dry, wet };
    if (!rendered) applyAb(live.current);
    node.onended = () => setPlaying(false);
    node.start();
    setPlaying(true);
  };
  const run = async () => {
    stop();
    try { setResult(await autoMaster(src, s, setBusy)); toast.success("Mastering completato!"); }
    catch { toast.error("Errore durante il mastering"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="mastering-studio">
      {allowUpload && (
        <label className={`${btn} justify-center w-full bg-emerald-500 text-[#151827] hover:bg-emerald-400 cursor-pointer py-4`} data-testid="mastering-upload-btn">
          <Upload className="h-4 w-4" /> Carica un file (WAV, MP3, FLAC)
          <input type="file" accept="audio/*" hidden onChange={onFile} data-testid="mastering-file-input" />
        </label>
      )}
      {busy && <p className="flex items-center gap-2 text-emerald-300 text-sm" data-testid="mastering-status"><Loader2 className="h-4 w-4 animate-spin" />{busy}</p>}
      {src && (
        <>
          <p className="text-sm text-zinc-300" data-testid="mastering-track-name">Brano: <b className="text-white">{name}</b></p>
          <StatsRow a={inStats} prefix="mastering-in" />
          <MasteringControls s={s} setS={(v) => { setS(v); setResult(null); }} genre={genre} setGenre={setGenre} mode={mode} setMode={setMode} />
          <div className="rounded-xl border border-sky-400/30 bg-sky-400/5 p-4 flex flex-wrap items-center gap-3" data-testid="mastering-reference">
            <Disc3 className="h-5 w-5 text-sky-300" />
            <p className="text-sm flex-1 min-w-[200px]">{ref ? <>Riferimento: <b>{ref.name}</b> · {ref.lufs.toFixed(1)} LUFS — tonalità e volume abbinati</> : "Brano di riferimento: carica un pezzo che ami e il master ne copia tonalità e volume."}</p>
            <label className={`${btn} border border-sky-400 text-sky-300 cursor-pointer`} data-testid="mastering-reference-btn">
              <Upload className="h-4 w-4" /> {ref ? "Cambia" : "Carica riferimento"}
              <input type="file" accept="audio/*" hidden onChange={onRef} data-testid="mastering-reference-input" />
            </label>
            <button onClick={toggleAuto} className={`${btn} ${auto ? "bg-sky-400 text-[#151827]" : "border border-sky-400/60 text-sky-200"}`} data-testid="mastering-autobalance-btn" title="Corregge in automatico le frequenze verso il bilanciamento tipico del genere scelto">
              <Sparkles className="h-4 w-4" /> {auto ? "Bilanciamento auto ON" : "Bilanciamento automatico"}
            </button>
            {ref && <button onClick={clearRef} className="text-zinc-400 hover:text-white" aria-label="Rimuovi riferimento" data-testid="mastering-reference-clear"><X className="h-4 w-4" /></button>}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => (playing ? stop() : play())} className={`${btn} border border-white/20 hover:border-emerald-400`} data-testid="mastering-preview-btn">
              {playing ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />} {playing ? "Stop" : "Anteprima live"}
            </button>
            <div className="inline-flex rounded-full border border-white/20 p-1" role="group" title="Confronto allo stesso volume">
              {[["orig", "Originale"], ["master", "Master"]].map(([k, l]) => (
                <button key={k} onClick={() => setAb(k)} data-testid={`mastering-ab-${k}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold ${ab === k ? (k === "master" ? "bg-emerald-500 text-[#151827]" : "bg-white text-[#151827]") : "text-zinc-300"}`}>{l}</button>
              ))}
            </div>
            <span className="text-[11px] text-zinc-400">A/B allo stesso volume, per giudicare il suono e non il volume</span>
            <button onClick={run} disabled={!!busy} className={`${btn} bg-hot text-[#151827] hover:bg-pink-400`} data-testid="mastering-run-btn"><Wand2 className="h-4 w-4" /> Avvia mastering</button>
          </div>
          <Spectrum before={inStats?.bands} after={result?.bands} />
          {result && (
            <div className="rounded-2xl border border-emerald-400/40 bg-emerald-400/5 p-5 space-y-4" data-testid="mastering-result">
              <p className="font-semibold">Master pronto{result.gainReduction < -0.1 ? ` · limiter ${result.gainReduction.toFixed(1)} dB di riduzione massima` : " · picchi già sotto il limite, nessun intervento del limiter"}</p>
              <StatsRow a={result} prefix="mastering-out" master />
              <div className="flex flex-wrap gap-3">
                <button onClick={() => (playing ? stop() : play(result.buffer, true))} className={`${btn} border border-white/20`} data-testid="mastering-play-result-btn"><Play className="h-4 w-4" /> Ascolta il master</button>
              </div>
              <MasteringExport buffer={result.buffer} name={name} />
              {actions?.(result, name, src)}
            </div>
          )}
        </>
      )}
    </div>
  );
};
